import * as R from "ramda";
import { useEffect, useState } from "react";

import ajaxUtil from "@/util/ajax";
import {
  showMediaOssUrl,
  uploadMediaOssUrl,
  previewMediaOssBaseUrlWithCdn,
} from "./widgets/upload-resource";
import { mapValues } from "@/util/object-utils";

const { ajaxv2, ajax } = ajaxUtil;

/**
 * 删除对象中的指定相同的值
 * @param obj Object {name:'a',age:1}
 * @param matchList Array<any> ['a']
 *
 * @returns Object {age:1}
 */
export const removeValuePatterns = (obj, matchList = []) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (!matchList.includes(value)) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const changeAliyunMediaToJfotoApi = (aliyun_media_id) => {
  return ajaxv2({
    url: `/api/v2/aliyun/ice/change/${aliyun_media_id}/tojfoto`,
    method: "GET",
  });
};

export const listAliyunResourcesApi = (data) => {
  //  /api/v2/aliyun/ice/order/:orderId/list
  const dataWithoutOrderId = R.omit(["orderId"], data);
  return ajaxv2({
    url: `/api/v2/aliyun/ice/order/${data.orderId}/list?${new URLSearchParams(
      removeValuePatterns(dataWithoutOrderId, [undefined, ""])
    ).toString()}`,
    method: "GET",
  });
};

export const getResourceList = listAliyunResourcesApi;

export const transformResourceItemToFullPath = (item) => {
  return {
    ...item,
    path:
      showMediaOssUrl +
      (item.path.startsWith("/") ? item.path.slice(1) : item.path),
  };
};

export const transformOssCoverUrl = (item) => {
  return {
    ...item,
    path: (() => {
      switch (item.type) {
        case "video":
          return item.path;
        case "image":
          return item.path + "?x-oss-process=image/resize,h_180,m_lfit";
        default:
          return item.path;
      }
    })(),
  };
};

export const mediaInfoListApi = (mediaids) => {
  return ajaxv2({
    url: `/api/v2/aliyun/ice/mediainfolist?mediaids=${mediaids.join(",")}`,
    method: "GET",
  });
  // return post("/api/v2/aliyun/ice/mediainfolist", { mediaids });
};

export const listAliyunResourcesApiWithMediaInfo = (data) => {
  return new Promise((resolve) => {
    listAliyunResourcesApi(data).then((res) => {
      const list = R.pathOr([], ["items"], res);
      if(list.length === 0) {
        resolve(res);
        return;
      }
      const mediaids = list.map(R.prop("aliyun_media_id"));
      mediaInfoListApi(mediaids).then((mediaInfoList) => {
        resolve({
          items: list.map((item) => {
            const mediaInfo = R.find(
              R.propEq(R.pathOr("", ["aliyun_media_id"], item), "MediaId"),
              mediaInfoList
            );
            const options = R.pathOr({}, ["options"], item);
            return {
              ...item,
              options: mapValues(options, (value, key) => {
                if (key === "coverUrl") {
                  return (value || "").replace(
                    uploadMediaOssUrl,
                    previewMediaOssBaseUrlWithCdn
                  );
                }
                return value;
              }),
              ...mediaInfo,
            };
          }),
          total: R.pathOr(0, ["total"], res),
        });
      });
    });
  });
};

export const getResourceDetailApi = (id) => {
  return ajaxv2({
    //  /api/v2/aliyun/ice/:id/detail
    url: `/api/v2/aliyun/ice/${id}/detail`,
    method: "GET",
  });
};

export const get = (url, params = {}) => {
  return ajaxv2({
    url,
    method: "GET",
    params,
  });
};


export const useTransformGet = (url, params = {}, options) => {
  const {
    deps = [],
    setData = R.T,
    transform = v => v,
    beforeSend = R.T,
  } = options;
  const [loading, setLoading] = useState(true);
  const [resData, setResData] = useState(undefined);
  useEffect(() => {
    if(!beforeSend()) {
      return;
    }
    setLoading(true);
    get(url, params)
      .then((res) => {
        const transformed = transform(res);
        setData(transformed);
        setResData(transformed);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [url, JSON.stringify(params), JSON.stringify(deps)]);

  return { loading, data: resData };
}

export const useGet = (url, params = {}, setData = R.T, deps = []) => {
  return useTransformGet(url, params, {setData, deps});
};




export const post = (url, data = {}) => {
  return ajaxv2({
    url,
    method: "POST",
    data,
  });
};

export const usePost = (url, data = {}, setData = R.T) => {
  const [loading, setLoading] = useState(true);
  const [resData, setResData] = useState(undefined);
  useEffect(() => {
    post(url, data)
      .then((res) => {
        setData(res);
        setResData(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [url, JSON.stringify(data)]);

  return { loading, data: resData };
};

export const useTransformPost = (url, data = {}, options) => {
  const {
    deps = [],
    setData = R.T,
    transform = v => v,
    beforeSend = R.T,
  } = options;
  const [loading, setLoading] = useState(true);
  const [resData, setResData] = useState(undefined);
  useEffect(() => {
    if(!beforeSend()) {
      return;
    }
    setLoading(true);
    post(url, data)
      .then((res) => {
        const transformed = transform(res);
        setData(transformed);
        setResData(transformed);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [url, JSON.stringify(data), JSON.stringify(deps)]);

  return { loading, data: resData };
}


export const deleteMedia = (orderId, id) => {
  return post(`/api/v2/aliyun/ice/order/${orderId}/${id}/del`);
};

export const deleteMediaBatch = (orderId, ids) => {
  //  each request with delay of 200ms
  return Promise.all(
    ids.map((id, index) => {
      return new Promise((resolve) => {
        setTimeout(() => {
          deleteMedia(orderId, id).then(resolve);
        }, index * 200);
      });
    })
  );
};

/**
 * /api/v2/aliyun/ice/project/create
 *
 * @param data Object
 *   "title": "test-title",
 *   "orderId":111
 */
export const createProject = (data) => {
  return post(`/api/v2/aliyun/ice/project/create`, data);
};

//  /api/v2/aliyun/ice/project/list
export const listProjects = (queryObject = {}) => {
  return ajaxv2({
    url: `/api/v2/aliyun/ice/project/list?${new URLSearchParams(
      removeValuePatterns(queryObject, [undefined, ""])
    ).toString()}`,
    method: "GET",
  });
};

//  /api/v2/aliyun/ice/common
export const common = (data) => {
  return post(`/api/v2/aliyun/ice/project/common`, data);
};

export const getEditingProject = (ProjectId) => {
  return common({
    action: "GetEditingProject",
    params: {
      ProjectId,
    },
  });
};

//  UpdateEditingProject
export const updateEditingProject = (ProjectId, data = {}) => {
  return common({
    action: "UpdateEditingProject",
    params: {
      ProjectId,
      ...data,
    },
  });
};

/**
 * 生成视频, SubmitMediaProducingJob
 * @param ProjectId
 * @param params
 * @returns {Promise<*>}
 */
export const produceEditingProjectVideo = (ProjectId, params) => {
  return common({
    action: "SubmitMediaProducingJob",
    params: {
      ProjectId,
      ...params,
    },
  });
};

/**
 * 生成字幕: SubmitASRJob
 */
export const submitASRJob = (params) => {
  return common({
    action: "SubmitASRJob",
    params: {
      ...params,
    },
  });
};

/**
 * 获取智能分析结果: GetSmartHandleJob
 */
export const getSmartHandleJob = (params) => {
  return common({
    action: "GetSmartHandleJob",
    params: {
      ...params,
    },
  });
};

/**
 * 获取智能分析结果
 */
export const getAiResult = (url) => {
  return post(`/api/v2/aliyun/ice/project/ai/tool`, { url });
};

export const getFileContent = (url) => {
  return post("/api/v2/aliyun/ice/getHttpFile", { url });
};

/**
 * 提交AI分析任务
 */
export const submitAIJob = (params) => {
  return common({
    action: "SubmitMediaAiAnalysisJob",
    params: {
      ...params,
    },
  });
};

export const getOrderDetail = (orderId) => {
  //  https://admin.jfoto.cn/api/order/113241
  return ajax({
    url: "/api/order/" + orderId,
  });
};

//  批量公开或隐藏 , api/aliyun/ice/order/:orderId/media_sources/batch_hidden
//
//
// {
//   "mediaIds":[
//     46,47
//   ],
//   "hidden":0
// }
export const batchHidden = (orderId, mediaIds, hidden) => {
  return post(
    `/api/v2/aliyun/ice/order/${orderId}/media_sources/batch_hidden`,
    {
      mediaIds,
      hidden,
    }
  );
};

//  批量删除 api/aliyun/ice/order/:orderId/media_sources/batch_del
//
// {
//   "mediaIds":[
//     46,47
//   ]
// }
export const batchDel = (orderId, mediaIds) => {
  return post(`/api/v2/aliyun/ice/order/${orderId}/media_sources/batch_del`, {
    mediaIds,
  });
};

/**
 * 给项目添加媒体资源
 * /api/v2/aliyun/ice/project/:projectId/media_sources/add
 */
export const projectMediaSourcesAdd = (projectId, mediaIds) => {
  return post(`/api/v2/aliyun/ice/project/${projectId}/media_sources/add`, {
    mediaIds,
  });
};

/**
 * 获取单个工程详情
 * /api/aliyun/ice/project/:pkid/detail
 */
export const projectDetail = (projectId) => {
  return ajaxv2({
    url: `/api/v2/aliyun/ice/project/${projectId}/detail`,
    method: "GET",
  });
};

/**
 * 获取树形结构
 * /api/aliyun/ice/media_sources/order/80667/date_area
 */
export const projectMediaSourcesOrderDateArea = (orderId) => {
  return ajaxv2({
    url: `/api/v2/aliyun/ice/media_sources/order/${orderId}/date_area`,
    method: "GET",
  });
};

/**
 * 获取tagList针对特定的订单
 * /api/media_sources/orders/80667/tags/list
 *
 *
 *        *        "tags": [
 *        *             {
 *        *                 "tag_id": 1,
 *        *                 "count": 1
 *        *             },
 *        *             {
 *        *                 "tag_id": 2,
 *        *                 "count": 2
 *        *             }
 *        *         ],
 *        *         "tags_detail": [
 *        *             {
 *        *                 "id": 1,
 *        *                 "name": "luoxun"
 *        *             },
 *        *             {
 *        *                 "id": 2,
 *        *                 "name": "Google"
 *        *             }
 *        *         ]
 *        *
 *        *         => tags_detail.map => [{
 *        *           id: 1,
 *        *           name: "luoxun"
 *        *           count: 1
 *        *         }, {
 *        *         id: 2,
 *        *         name: "Google"
 *        *         count: 2
 *        *         }]
 *        *
 *
 */
export const projectMediaSourcesOrdersTagsList = (orderId) => {
  return ajaxv2({
    url: `/api/v2/media_sources/orders/${orderId}/tags/list`,
    method: "GET",
  });
};

/**
 * 添加tag
 * /api/media_sources/order/:orderId/tags/relation/add
 */
export const addTag = (orderId, media_source_id, checkedIdOrIds) => {
  return post(`/api/v2/media_sources/order/${orderId}/tags/relation/add`, {
    tag_id: Array.isArray(checkedIdOrIds) ? checkedIdOrIds : [checkedIdOrIds],
    media_source_id,
  });
};

/**
 * 删除tag
 * /api/media_sources/order/:orderId/tags/relation/add
 */
export const delTag = (orderId, media_source_id, checkedId) => {
  return post(`/api/v2/media_sources/order/${orderId}/tags/relation/del`, {
    tag_id: checkedId,
    media_source_id,
  });
};

/**
 * 获取一个新的详情
 * /api/v2/media_sources/:media_sources_id/detail
 */
export const mediaSourcesDetail = (media_source_id) => {
  return ajaxv2({
    url: `/api/v2/media_sources/${media_source_id}/detail`,
    method: "GET",
  });
};

/**
 * /api/media_sources/order/:orderId/tag/add
 */
export const addTagApi = (orderId, name, auth) => {
  return post(`/api/v2/media_sources/order/${orderId}/tag/add`, {
    orderId,
    name,
    auth,
  });
};

/**
 * /api/v2/aliyun/ice/media_sources/saveone/:pkId
 */
export const mediaSourcesSaveOne = (pkId, data) => {
  return post(`/api/v2/aliyun/ice/media_sources/saveone/${pkId}`, data);
};

/**
 * 修改project list中的一个的名称
 * /api/v2/aliyun/ice/project/:pkid/saveone
 */
export const projectSaveOne = (pkid, data) => {
  return post(`/api/v2/aliyun/ice/project/${pkid}/saveone`, data);
};

export const getSingleProjectIdByOrderId = (orderId) => {
  return new Promise(async (resolve) => {
    const list = (await listProjects({ orderId })) || [];
    if (list.length === 0) {
      const createResponse = await createProject({
        title: "默认项目",
        orderId,
      });
      resolve(R.pathOr(null, ["aliyun_project_id"], createResponse));
    } else {
      resolve(R.pathOr(null, [0, "aliyun_project_id"], list));
    }
  });
};

/**
 * 根据timeline生成一个新视频
 * inOutArray: [{In:number,Out:number}]
 */
export const createVideoByTimeline = (
  mediaId,
  templateId,
  fileName,
  inOutArray,
  userId,
  extra = {}
) => {
  // return new Promise((resolve, reject) => {
  //   const orderId = R.pipe(
  //     R.pathOr("", ["location", "pathname"]),
  //     R.split("/"),
  //     R.reverse,
  //     R.nth(1),
  //     R.defaultTo("0")
  //   )(window);
  //   getSingleProjectIdByOrderId(orderId).then(async (projectId) => {
  //     const url = getRandomFullPathOfMp4();
  //     try {
  //       const response = await common({
  //         action: "SubmitMediaProducingJob",
  //         params: {
  //           ProjectId: projectId,
  //           Timeline: JSON.stringify({
  //             VideoTracks: inOutArray.map((item) => ({
  //               VideoTrackClips: [
  //                 {
  //                   ...item,
  //                   MediaId: mediaId,
  //                 },
  //               ],
  //             })),
  //             AudioTracks: [],
  //             SubtitleTracks: [],
  //             EffectTracks: [],
  //           }),
  //           OutputMediaTarget: "oss-object",
  //           MediaMetadata: JSON.stringify({
  //             Title: fileName,
  //           }),
  //           OutputMediaConfig: JSON.stringify({
  //             MediaURL: url,
  //           }),
  //         },
  //       });
  //       resolve(response);
  //     } catch (e) {
  //       reject(e);
  //     }
  //   });
  // });

  return post("/api/v2/goldtemplate/subjob", {
    media_source_id: mediaId,
    template_id: templateId,
    gold_string: inOutArray,
    name: fileName,
    auth_by: userId * 1,
    ...extra,
  });
};

export const checkMediaIsExist = (url) => {
  return post("/api/v2/aliyun/oss/media-resources/check", { url });
};



// WEBPACK FOOTER //
// ./src/containers/Video/aliyun-service-api.js